import { Backdrop, CircularProgress } from "@mui/material";
import {
  onAuthStateChanged,
  signOut as firebaseSignOut,
  User,
} from "firebase/auth";
import {
  createContext,
  ReactNode,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { useNetInfo } from "../hooks";
import { auth, database, firestore } from "../services/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { Client } from "../@data/client";
import { ref, set, onDisconnect } from "firebase/database";

export interface AuthContextModel {
  user: User;
  client: Client;
  loadingAuth: boolean;
  isLogged: boolean;
  isVisitant: boolean;
  signOut: (callback?: () => void) => void;
}

export const AuthContext = createContext<AuthContextModel>({
  user: {} as User,
  client: {} as Client,
  loadingAuth: true,
  isLogged: false,
  isVisitant: false,
  signOut: () => {},
});

export const AuthProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [user, setUser] = useState<User>({} as User);
  const [client, setClient] = useState<Client>({} as Client);
  const [loadingAuth, setLoadingAuth] = useState<boolean>(true);
  const { isOnline } = useNetInfo();
  const isLogged = !!user?.uid;
  const isVisitant = !!user?.isAnonymous;

  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      if (user) setUser(user);
      setLoadingAuth(false);
    });
  }, [isOnline]);

  useEffect(() => {
    if (user.uid) {
      const device = /iPhone|iPad|iPod/i.test(navigator.userAgent)
        ? "ios"
        : /Android/i.test(navigator.userAgent)
        ? "android"
        : "other";

      const userRef = ref(database, `users/${user.uid}`);
      onDisconnect(userRef).remove();

      if (!!user?.isAnonymous) {
        setClient((oldState) => ({ ...oldState, name: "Visitante" }));
        set(userRef, { device, isClient: false });
        return () => {
          user.delete();
        };
      } else {
        let userClientData = {} as Client;
        getDoc(doc(firestore, "clients", user.uid)).then((docSnap) => {
          if (docSnap.exists()) {
            userClientData = docSnap.data() as Client;
            setClient(userClientData);
            updateDoc(docSnap.ref, {
              lastAccess: new Date().getTime(),
            });
          }
        });

        if (userClientData.unitId)
          set(userRef, {
            device,
            isClient: true,
            unitId: userClientData.unitId,
          });
        else set(userRef, { device, isClient: true });
      }
    }
  }, [user]);

  const signOut: AuthContextModel["signOut"] = useCallback((callback) => {
    firebaseSignOut(auth).then(() => {
      setUser({} as User);
      if (callback) callback();
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        client,
        isLogged,
        isVisitant,
        loadingAuth,
        signOut,
      }}
    >
      {loadingAuth ? (
        <Backdrop open>
          <CircularProgress />
        </Backdrop>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
